import { installTranslateGuard } from './lib/translate-guard';
import { StrictMode } from 'react';
import { hydrateRoot, createRoot } from 'react-dom/client';
import { hydrate } from '@tanstack/react-query';
import type { DehydratedState } from '@tanstack/react-query';
import { App, preloadRouteChunk } from './app';
import { queryClient } from './lib/query-client';
import { captureAdClickIds } from './lib/meta-cookies';
import { startEarlySearch } from './lib/early-search';
import { initPostHog } from './lib/posthog';
import './styles/globals.css';

// FIRST, before React touches the DOM: Chrome's automatic page translation
// rewrites text nodes behind React's back, and React's next removeChild /
// insertBefore on one of them throws and takes the whole route into the
// «Страница не открылась» boundary (facebook/react#11538). The guard turns
// those two impossible operations into no-ops. Browser-only — this file never
// runs under SSR/prerender. See lib/translate-guard.ts.
installTranslateGuard();

// Rehydrate the query cache that entry-server serialized into the prerendered
// HTML (window.__RQ_STATE__), into the SAME singleton App uses. This makes the
// first client render see the server's data (status: 'success'), so it reuses the
// SSR DOM instead of flashing loading states + refetching on mount. A background
// refetch still runs for stale queries — non-blocking, off the LCP path.
const rqState = (window as unknown as { __RQ_STATE__?: DehydratedState }).__RQ_STATE__;
if (rqState) hydrate(queryClient, rqState);

// MiLicita: a cold `/?q=…` load sends its search now, while the page hydrates,
// instead of after. No-op on UZ/KZ and on every other URL — see the module.
startEarlySearch();

// Persist fbclid + UTM params from the landing URL so they survive SPA nav to
// the signup/checkout step. The Meta Pixel base + initial PageView load from
// the snippet in index.html.
captureAdClickIds();
// NOTE: the Pixel's external_id is seeded in index.html, on the single init
// that runs before the initial PageView. It cannot be set from here — the
// pixel accepts user data only while its stored userData is empty, so a second
// init is rejected as DUPLICATE_PIXEL_ID.
// Behavioral analytics + session replay (ADR 0002). Lazy + dormant unless
// VITE_POSTHOG_KEY is set; the SDK dynamic-imports so it never blocks hydration.
initPostHog();

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element not found');

// If SSR prerendered this page, hydrate; otherwise CSR mount
function mount(root: HTMLElement): void {
  if (root.hasChildNodes()) {
    hydrateRoot(
      root,
      <StrictMode>
        <App />
      </StrictMode>,
    );
  } else {
    createRoot(root).render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  }
}

// A prerendered route whose component lives in a code-split chunk (/guide and
// /guide/<slug> — see preloadRouteChunk) must have that chunk in hand BEFORE the
// first render: React.lazy suspends even on a cached module, and the prerendered
// HTML has no Suspense boundary to suspend into, so hydration would mismatch and
// throw the prerendered article away. Every other path returns null here and
// mounts synchronously, unchanged.
const pendingChunk = preloadRouteChunk(window.location.pathname);
if (pendingChunk) {
  // Mount either way: a chunk that 404s (stale tab across a deploy) must not
  // leave the page unmounted — the route then renders the error/reload path
  // `lib/lazy-route.ts` owns.
  const mountRoot = () => mount(rootEl);
  void pendingChunk.then(mountRoot, mountRoot);
} else {
  mount(rootEl);
}
